import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Chip,
  Divider,
  Drawer,
  Stack,
  Typography,
} from "@mui/material";

import {
  Disclose,
  DrawerHead,
  ErrorState,
  Loading,
  Markdown,
  Stat,
  Stats,
  Term,
  Tooltip,
  modelLabel,
} from "@lopie/kernel-ui";

/**
 * One agent, asked about by name.
 *
 * ## This is the record; the card is the summary
 *
 * The chart answers *who is this, is it working, what does it cost, is it
 * allowed to write* and stops. Everything a person opens an agent to find out
 * is here, and it is here rather than on the card for a reason that is about
 * honesty rather than taste: the card had every one of these facts on it, and
 * the page was eight screens tall, and a qualification nobody scrolls to is
 * not a qualification. Moving the detail one click in is what makes it
 * readable enough to be read.
 *
 * Both halves of what an agent is live here side by side, which is the whole
 * argument:
 *
 *     `manifests/<name>.json` says what the *kernel* will let this app touch;
 *     `agents/<name>/agent.py` says what *Anthropic* will let the agent touch.
 *
 * **Neither is derived from the other and this screen must not imply that
 * they are.** A tool listed under the configuration that the manifest does
 * not permit is refused on every use; a manifest widened without touching the
 * definition gives the agent nothing. So they are two sections under two
 * headings, never one merged list — two lists under one label would read as
 * one decision.
 *
 * ## Why the writes are fetched rather than filtered from what the chart has
 *
 * The chart reads one window of the whole log and every card counts its own
 * name inside it. That is the right trade for a chart — one request, every
 * lane — and it is the wrong one here, because the question changes. On the
 * chart the question is *is this agent working*; here it is *what has this
 * agent done*, and the answer to the second cannot be a by-product of a page
 * sized for the first.
 *
 * So this asks the kernel: `GET /audit?agent=<name>`. `agent` is one of the
 * six attribute filters the endpoint accepts, which means the kernel walks
 * every action partition and matches after the read — the response says as
 * much in `cost`, and says when the walk stopped early. Both are rendered. A
 * drawer that showed six rows from a walk that gave up would be answering
 * "that is everything this agent has done" by accident.
 *
 * ## What is here that a card could never carry
 *
 * **The whole ceiling, both halves.** The card says *reads 2 types
 * unattended*; this says which two and under which tags. `reads` — the
 * with-a-person half — is here too, and for these apps it is empty, which is
 * a fact worth being able to look up rather than infer: a user token for this
 * client would see almost nothing.
 *
 * **The human/machine lens.** `mode` shipped with LO-55 and nothing read it
 * until this drawer. "Every unattended write" is a question no other stack in
 * this space can answer about its own agents, and the answer is one column
 * here.
 *
 * **`on_behalf_of`, and the fact that it is null.** An agent acting with
 * nobody behind it is the thing a person is actually being asked to sign off
 * on, and a screen that quietly omitted the empty column would be hiding the
 * subject of the decision.
 */

/** Rows to ask for. Larger than the chart's window because it is one agent. */
const ROWS = 50;

/**
 * One write, as a block rather than a table row.
 *
 * A table was the first version and it was wrong here for a reason worth
 * writing down: five columns in a 560px drawer means the last two scroll out
 * of sight, and the last two are `mode` and `on_behalf_of` — the whole reason
 * this view exists. A table puts the most important field where a person has
 * to go looking for it.
 */
function Write({ row }) {
  const mode = row.provenance?.mode;
  const behalf = row.provenance?.on_behalf_of;
  return (
    <Box sx={{ py: 1.25, borderTop: 1, borderColor: "divider", minWidth: 0 }}>
      <Stack
        direction="row"
        spacing={1}
        sx={{ alignItems: "baseline", flexWrap: "wrap", justifyContent: "space-between" }}
      >
        <Mono>{row.provenance?.action}</Mono>
        <Typography variant="body2" color="text.secondary">
          {new Date(row.at).toLocaleString()}
        </Typography>
      </Stack>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ display: "block", overflowWrap: "anywhere", mb: 0.75 }}
      >
        {row.subject?.kind === "record" ? "" : `${row.subject?.kind}: `}
        <Mono>{row.subject?.label}</Mono>
      </Typography>
      <Stack direction="row" spacing={0.5} sx={{ flexWrap: "wrap" }}>
        <Tooltip
          title={
            mode
              ? "Recorded by the kernel itself at the moment of the write. The app cannot claim this, only have it observed."
              : "This write predates the kernel recording whether anybody was signed in. It is not a claim that somebody was."
          }
        >
          <Chip
            size="small"
            variant="outlined"
            label={mode || "not recorded"}
            sx={
              mode === "unattended"
                ? {
                    color: "kernel.unattended",
                    borderColor: "kernel.unattended",
                  }
                : undefined
            }
          />
        </Tooltip>
        <Tooltip
          title={
            behalf
              ? "The signed-in person this write was made for."
              : "No signed-in person was behind this write. That is the ordinary answer for an agent running to a schedule, and it is the thing worth knowing about it."
          }
        >
          <Chip
            size="small"
            variant="outlined"
            label={behalf ? `for ${behalf}` : "nobody behind it"}
          />
        </Tooltip>
      </Stack>
    </Box>
  );
}

function Mono({ children, ...rest }) {
  return (
    <Typography variant="mono" component="span" {...rest}>
      {children}
    </Typography>
  );
}

const count = (n) => Number(n || 0).toLocaleString("en-US");

function dollars(usd) {
  if (!usd) return "$0.00";
  return usd < 0.01 ? "<$0.01" : `$${usd.toFixed(2)}`;
}

function Grants({ grants, empty }) {
  if (!grants?.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        {empty}
      </Typography>
    );
  }
  return (
    <Stack direction="row" spacing={0.5} sx={{ flexWrap: "wrap" }}>
      {grants.map((grant) => (
        <Tooltip
          key={grant.type}
          title={
            grant.tags.length
              ? `Only records tagged ${grant.tags.join(", ")}.`
              : "Untagged records of this type only. Counterintuitively this is the narrowest permission there is, not the widest."
          }
        >
          <Chip
            size="small"
            variant="outlined"
            label={
              grant.tags.length
                ? `${grant.type} · ${grant.tags.join(", ")}`
                : `${grant.type} · untagged only`
            }
          />
        </Tooltip>
      ))}
    </Stack>
  );
}

/** What a channel is called on screen. Plain, because the point is the risk. */
const CHANNEL = { email: "email", sms: "text", voice: "phone" };

/**
 * Whether this agent can reach a person outside the kernel, and how.
 *
 * Its own line rather than a chip among the verbs, because it is a different
 * kind of fact. Every other permission touches a record the kernel still
 * holds and can be checked against it; this one ends at a mailbox or a
 * handset, and once it has happened there is nothing to check.
 *
 * The reassuring case is printed too. "Cannot contact anybody outside this
 * kernel" is the answer for almost every agent and is worth reading rather
 * than inferring from an absence.
 */
function Reach({ reach }) {
  const attended = reach?.attended || [];
  const unattended = reach?.unattended || [];
  const all = [...new Set([...attended, ...unattended])].sort();

  if (!all.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        Cannot contact anybody outside this kernel. It holds no outbound verb,
        so there is no mailbox or handset it could reach.
      </Typography>
    );
  }

  // Unattended is the sharper half and gets said separately: an agent that
  // may text only when nobody is signed in can wake somebody at three in the
  // morning with no person in the loop.
  const alone = unattended.filter((c) => !attended.includes(c));

  return (
    <Stack spacing={0.5}>
      <Typography variant="body2" sx={{ color: "kernel.unattended", fontWeight: 600 }}>
        Can contact people outside this kernel by{" "}
        {all.map((c) => CHANNEL[c] || c).join(" and ")}.
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {alone.length
          ? `It may do so ${alone.map((c) => CHANNEL[c] || c).join(" and ")} with nobody signed in, so a schedule or an alarm can reach somebody with no person in the loop.`
          : "Only for a signed-in person — nothing it does unattended can leave the building."}
      </Typography>
    </Stack>
  );
}

function Block({ label, children }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography
        variant="overline"
        color="text.secondary"
        sx={{ display: "block", lineHeight: 1.6 }}
      >
        {label}
      </Typography>
      {children}
    </Box>
  );
}

/**
 * A heading with a rule, for the four things this drawer is made of.
 *
 * The drawer is long by design — it is the place the detail went — so it
 * needs structure a person can skim. Four rules and four short headings is
 * that structure; a single stack of labelled fields is not.
 */
function Heading({ children, note }) {
  return (
    <Box sx={{ mb: 1.5 }}>
      <Typography
        variant="overline"
        sx={{
          display: "block",
          pb: 0.5,
          borderBottom: 1,
          borderColor: "divider",
          color: "text.primary",
        }}
      >
        {children}
      </Typography>
      {/* The rule alone was enough when everyone reading it had built it.
          Five short labels that each make sense only once you already know
          what they refer to is five labels that tell a new person nothing. */}
      {note ? (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: 1, maxWidth: "62ch" }}
        >
          {note}
        </Typography>
      ) : null}
    </Box>
  );
}

/**
 * What Anthropic will let this agent do, beside what the kernel will (LO-110).
 *
 * The model it thinks with, the prompt it was given, the tools it was handed.
 * Until LO-110 all of it was visible to anybody who cloned the repo and to
 * nobody else.
 *
 * `source` is the field to read first when something looks wrong. It names
 * the committed file the definition came from — or says `changed here`, which
 * means somebody edited it through the kernel and the file is now behind.
 */
function Configured({ config }) {
  if (!config) {
    return (
      <Typography variant="body2" color="text.secondary">
        Nothing to show yet. This agent has been declared and permitted, but
        the part that says how it thinks — its model and its instructions — has
        never been loaded into this kernel, so there is nothing here to read.
        Whoever set the agent up needs to publish it once.
      </Typography>
    );
  }

  const tools = config.tools || [];
  const servers = config.mcp_servers || [];

  return (
    <Stack spacing={1.5}>
      <Stack direction="row" spacing={0.75} sx={{ flexWrap: "wrap" }}>
        {config.model ? (
          <Tooltip title={config.model}>
            <Chip size="small" variant="outlined" label={modelLabel(config.model)} />
          </Tooltip>
        ) : null}
        {config.effort ? (
          <Tooltip title="How hard the model thinks before answering. Higher costs more and is not automatically better.">
            <Chip
              size="small"
              variant="outlined"
              label={`thinks ${config.effort}`}
            />
          </Tooltip>
        ) : null}
        <Tooltip
          title={
            config.version
              ? "This setup is live — Anthropic reported this version when it was last published."
              : "Saved here but never published, so the agent is still running with its previous setup."
          }
        >
          <Chip
            size="small"
            variant="outlined"
            label={config.version ? `live · v${config.version}` : "not published yet"}
            sx={
              config.version
                ? undefined
                : { color: "kernel.attention", borderColor: "kernel.attention" }
            }
          />
        </Tooltip>
      </Stack>

      <Block label="Tools it was handed">
        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75 }}>
          What it can actually operate. A tool here still only reaches what the
          permissions above allow — this list cannot widen them.
        </Typography>
        {tools.length ? (
          <Stack direction="row" spacing={0.5} sx={{ flexWrap: "wrap" }}>
            {tools.map((tool) => (
              <Chip
                key={tool.name || tool.type}
                size="small"
                variant="outlined"
                label={tool.name || tool.type}
                sx={{ fontFamily: "ui-monospace, monospace" }}
              />
            ))}
          </Stack>
        ) : (
          <Typography variant="body2" color="text.secondary">
            None. It can think, and it cannot operate anything.
          </Typography>
        )}
        {servers.length ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            reaches{" "}
            {servers.map((server) => server.url || server.name).join(", ")}
          </Typography>
        ) : null}
      </Block>

      {config.system ? (
        <Disclose
          align="row"
          label={`Read the prompt it was given (${config.system.length.toLocaleString()} characters)`}
        >
          {/* Rendered rather than dumped. These prompts are written in
              Markdown — headings, numbered procedures, bold run-in rules —
              and a `<pre>` showed somebody the `#` and `**` instead of the
              structure they carry, which is the one thing that makes a three
              thousand character instruction readable at all. */}
          <Box
            sx={{
              p: 1.5,
              maxHeight: 420,
              overflow: "auto",
              bgcolor: "action.hover",
              borderRadius: 1,
            }}
          >
            <Markdown>{config.system}</Markdown>
          </Box>
        </Disclose>
      ) : null}

      {config.applied_by ? (
        <Typography variant="body2" color="text.secondary">
          Published by {config.applied_by}.
        </Typography>
      ) : null}
    </Stack>
  );
}

export default function AgentDrawer({ agent, app, activity, get, onClose }) {
  const [page, setPage] = useState(null);
  const [failure, setFailure] = useState(null);

  const name = agent?.name;

  const load = useCallback(async () => {
    if (!name) return;
    setPage(null);
    setFailure(null);
    try {
      setPage(await get("/audit", { agent: name, limit: ROWS, sort: "desc" }));
    } catch (e) {
      setFailure(e);
    }
  }, [get, name]);

  useEffect(() => {
    load();
  }, [load]);

  const items = page?.items || [];
  const unattended = items.filter(
    (row) => row.provenance?.mode === "unattended",
  ).length;
  const withPerson = items.filter((row) => row.provenance?.on_behalf_of).length;
  const spend = agent?.spend;

  return (
    <Drawer
      anchor="right"
      open={Boolean(agent)}
      onClose={onClose}
      slotProps={{
        paper: { sx: { width: { xs: "100%", sm: 600 }, maxWidth: "100%" } },
      }}
    >
      {agent ? (
        <Box sx={{ p: { xs: 2, sm: 3 }, minWidth: 0 }}>
          <DrawerHead
            title={agent.label || agent.name}
            id={`${agent.name} · part of ${agent.app_type}${
              app?.version ? ` · permissions v${app.version}` : ""
            }`}
            onClose={onClose}
            lede={
              <>
                {agent.does} This is the full record for one{" "}
                <Term name="agent">agent</Term> in {agent.division}: everything
                it is permitted to reach, how it has been set up, what its runs
                have cost, and every change it has actually made. It is a read
                — nothing on this panel alters anything.
              </>
            }
          />

          {/* The same three figures the card showed, so opening a card does
              not feel like arriving somewhere else — and here they carry the
              full basis rather than the short label. */}
          <Stats sx={{ mb: 2.5 }}>
            <Stat
              value={
                spend?.runs
                  ? `${spend.complete ? "" : "≥ "}${dollars(spend.usd)}`
                  : "—"
              }
              label="spent so far"
              tone={spend?.complete === false ? "attention" : undefined}
              title={spend?.basis}
            />
            <Stat
              value={spend?.runs ? count(spend.runs) : "—"}
              label="times it has run"
            />
            <Stat
              value={activity ? count(activity.count) : "—"}
              label="changes in the recent window"
              title={
                activity
                  ? `Out of the ${activity.window} most recent changes across every agent. The list further down is a separate, deeper search for this agent's own.`
                  : undefined
              }
            />
          </Stats>

          <Divider sx={{ mb: 2.5 }} />

          <Heading
            note={
              <>
                The outer limit, set by a reviewed file rather than by anything
                on these screens. Nothing the agent does while running can
                widen it, and a request for anything outside it is refused — so
                this is the list to read if the question is &ldquo;what is the
                worst this thing could do&rdquo;.
              </>
            }
          >
            What it is allowed to touch
          </Heading>
          <Stack spacing={2} sx={{ mb: 3 }}>
            <Block label="Can it reach the outside world">
              <Reach reach={agent.reach} />
            </Block>

            <Block label="Changes it may make">
              {app?.actions?.length ? (
                <Stack direction="row" spacing={0.5} sx={{ flexWrap: "wrap" }}>
                  {app.actions.map((verb) => (
                    <Chip
                      key={verb}
                      size="small"
                      variant="outlined"
                      label={verb}
                      sx={{ fontFamily: "ui-monospace, monospace" }}
                    />
                  ))}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Nothing. This app declares no action at all.
                </Typography>
              )}
            </Block>

            <Block label="What it may look at on its own">
              <Grants
                grants={app?.service_reads}
                empty="Nothing. With no person behind a request this app reads nothing at all."
              />
            </Block>

            <Block label="What it may look at for a signed-in person">
              <Grants
                grants={app?.reads}
                empty="Nothing. A user token for this client could read no business type at all, which is narrower than the unattended half above and is deliberate: nobody signs into this app."
              />
            </Block>
          </Stack>

          <Heading note="The other half: the model it thinks with, how hard it thinks, and the tools it was handed. Separate from the permissions above and not derived from them — a tool here that the permissions do not allow simply gets refused every time.">
            How it has been set up
          </Heading>
          <Box sx={{ mb: 3 }}>
            <Configured config={agent.config} />
          </Box>

          {spend?.runs ? (
            <>
              <Heading note="Priced from what each run reported, at Anthropic&rsquo;s published rates. An estimate of list price, not an invoice.">
                What its runs have cost
              </Heading>
              <Stack spacing={1.5} sx={{ mb: 3 }}>
                <Stats>
                  <Stat
                    value={count(spend.input_total)}
                    label="sent to the model"
                    title="Everything fed to the model across every recorded run, counted in tokens — roughly three quarters of a word each. Repeated context bills at a tenth of the rate, and that discount is already applied here."
                  />
                  <Stat
                    value={count(spend.output)}
                    label="written by the model"
                    title="Output across every recorded run, counted in tokens. Output is billed at several times the rate of input."
                  />
                  <Stat
                    value={
                      spend.last_run_usd ? `$${spend.last_run_usd.toFixed(4)}` : "—"
                    }
                    label="the last one cost"
                  />
                </Stats>
                {!spend.complete ? (
                  <Typography variant="body2" sx={{ color: "kernel.attention" }}>
                    At least one run used a model this kernel holds no price
                    for, so the total is a floor rather than a sum.
                  </Typography>
                ) : null}
                {/* The kernel's own sentence, not one written here. A caption
                    a screen invents is a caption that drifts from what the
                    number actually is. */}
                <Typography variant="body2" color="text.secondary">
                  {spend.basis}
                </Typography>
              </Stack>
            </>
          ) : (
            <>
              <Heading note="Priced from what each run reported, at Anthropic&rsquo;s published rates. An estimate of list price, not an invoice.">
                What its runs have cost
              </Heading>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                No run has been recorded through the kernel, so there is
                nothing to price. That is not the same as never having run —
                anything driven from a terminal before this existed left a row
                in the audit log and none here.
              </Typography>
            </>
          )}

          <Heading note="Every change this agent has actually made, newest first. The kernel writes this record itself, so an agent cannot leave something out of it.">
            What it has changed
          </Heading>
          {failure ? (
            <ErrorState error={failure} onRetry={load} />
          ) : !page ? (
            <Loading label={`Asking the kernel for ${name}'s writes`} />
          ) : (
            <Stack spacing={1.5}>
              <Stack direction="row" spacing={0.5} sx={{ flexWrap: "wrap" }}>
                <Chip
                  size="small"
                  variant="outlined"
                  label={`${items.length} write${items.length === 1 ? "" : "s"}`}
                />
                <Tooltip title="Whether a person was in front of it. The kernel records this on every write; older writes made before it started recording are counted in neither number.">
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`${unattended} unattended`}
                    sx={{
                      color: "kernel.unattended",
                      borderColor: "kernel.unattended",
                    }}
                  />
                </Tooltip>
                <Tooltip title="Writes that were made for a named, signed-in person. An agent running to a schedule has nobody behind it, which is the ordinary case and the one worth knowing about.">
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`${withPerson} for a person`}
                  />
                </Tooltip>
              </Stack>

              {items.length ? (
                <Box sx={{ minWidth: 0 }}>
                  {items.map((row) => (
                    <Write key={row.event} row={row} />
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No write in the log names this agent. It is declared and
                  bounded and has not acted — which is a real state, and the
                  one every agent is in before its first run.
                </Typography>
              )}

              {page.stopped_early ? (
                <Alert
                  severity="warning"
                  variant="outlined"
                  sx={{ borderColor: "kernel.attention" }}
                >
                  <AlertTitle>This stopped early</AlertTitle>
                  {page.stopped_detail}
                </Alert>
              ) : null}

              {page.cost ? (
                <Typography variant="body2" color="text.secondary">
                  {page.cost}
                </Typography>
              ) : null}
            </Stack>
          )}
        </Box>
      ) : null}
    </Drawer>
  );
}
